import {Button} from "primereact/button";
import {useTranslation} from "react-i18next";

import {selectSegmentCandidates} from "../../redux/objectParametersSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {getResourceColor} from "./paramHooks";

interface SegmentConflictOptionsProps {
    /** Segment key to its translated name. */
    valueLabels: Record<string, string>;
    /** Whether the field cannot be operated right now (read-only, or a write in flight). */
    disabled: boolean;
    /** The field's change handler, called the way an input control would. */
    onChange: (event: {target: {value: string}}) => void;
}

/**
 * The segments of the disagreeing sources, as buttons below the segment field — the counterpart of
 * `InputNumberConflictOptions` of the number fields, only below instead of beside the control: the
 * segment names are too long for an addon next to the dropdown. Rendered only while the sources
 * disagree, so it always has something to offer.
 *
 * A click writes that segment as the override and resolves the conflict with it. It deliberately goes
 * through `onChange` rather than the dropdown: for the entry already marked there, PrimeReact reports
 * no onChange, so the effective segment could not be confirmed that way at all.
 */
export default function SegmentConflictOptions({valueLabels, disabled, onChange}: SegmentConflictOptionsProps) {
    const {t} = useTranslation();
    const candidates = useRevaSelector(selectSegmentCandidates);

    if (candidates.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-2 mt-2">
            {/* p-error wie Label und Konflikt-Meldung des Feldes: die Zeile gehört sichtbar zum selben Konflikt. */}
            <span className="text-xs p-error">{t("object.segmentConflict.chooseSource")}</span>
            {candidates.map(({origin, segment}) => (
                <Button
                    key={segment}
                    className={`p-button-sm border-0 px-2 py-1 font-medium bg-${getResourceColor(origin)}`}
                    disabled={disabled}
                    onClick={() => onChange({target: {value: segment}})}
                >
                    {valueLabels[segment] ?? segment}
                </Button>
            ))}
        </div>
    );
}
